'use client';

import { environment } from '@/config/environment';
import { useDemoSession } from '@/contexts/DemoSessionProvider';
import { useTheme } from '@/contexts/ThemeContext';
import { getDemoFingerprint } from '@/lib/demo-fingerprint';
import { DEMO_LOCATION_MESSAGE_SOURCE } from '@/lib/demo-business-types';
import type { TDemoAppPage } from '@/interfaces/demoBusinessTypes';
import { createDemoAuthRetryGuard } from '@/lib/demo-auth-retry';
import {
  createDemoFrameAuthTrigger,
  isEngagementEvent,
} from '@/lib/demo-frame-auth';
import { type CSSProperties, useCallback, useEffect, useRef } from 'react';
import { trackInteraction } from '@/lib/analytics';

type TDemoAppExperience =
  | 'intake-form'
  | 'survey'
  | 'terms-and-conditions'
  | 'pass-terms-conditions';

interface DemoAppFrameProps {
  language: 'en' | 'zh';
  locationId: number;
  title: string;
  className: string;
  style?: CSSProperties;
  experience?: TDemoAppExperience;
  /** Customer-app page to open instead of the configured home page. */
  page?: TDemoAppPage;
  loading?: 'eager' | 'lazy';
  authenticateOnReady?: boolean;
  /** Where this demo sits, for website telemetry (booking_demo_* events). */
  trackingSurface?:
    | 'business_type_hero'
    | 'home_industry_demo'
    | 'feature_hero';
}

/**
 * One customer-app iframe plus the trusted parent bridge it needs for shared
 * demo authentication, live location changes, data refreshes and appearance.
 * Feature showcases can opt into eager authentication when their requested
 * screen needs a signed-in customer immediately; the general booking demo
 * continues to allocate a pooled customer only after real interaction.
 */
export default function DemoAppFrame({
  language,
  locationId,
  title,
  className,
  style,
  experience,
  page,
  loading = 'lazy',
  authenticateOnReady = false,
  trackingSurface,
}: DemoAppFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const frameLoaded = useRef(false);
  const currentLocationId = useRef(locationId);
  currentLocationId.current = locationId;
  const initialAppearance = useRef<'light' | 'dark' | null>(null);
  const appliedAppearance = useRef<'light' | 'dark' | null>(null);
  // One session re-mint per rejected token, few per frame load: a structural
  // rejection (stale persisted customer in the iframe) must not loop into 429.
  const authRetryGuard = useRef(createDemoAuthRetryGuard());
  // Parent-side sign-in: the visitor engaged with the page (trusted input)
  // and this frame is on screen. Complements the iframe's own `interaction`
  // report, which never fires for a visitor who only scrolls the parent.
  const frameAuthTrigger = useRef(createDemoFrameAuthTrigger());
  const visitorEngagedRef = useRef(false);
  const frameVisibleRef = useRef(false);
  const trackedViewRef = useRef(false);
  const trackedEngagementRef = useRef(false);
  const accessTokenRef = useRef<string | null>(null);
  const { mounted: themeMounted, resolvedTheme } = useTheme();
  const {
    accessToken: sessionAccessToken,
    locationId: sessionLocationId,
    dataRevision,
    ensureSession,
    refreshSession,
    setLocationId,
  } = useDemoSession();
  const accessToken =
    sessionLocationId === locationId ? sessionAccessToken : null;
  accessTokenRef.current = accessToken;
  const demoBaseUrl = environment.api.demoAppUrl;
  const demoOrigin = new URL(demoBaseUrl).origin;

  if (themeMounted && initialAppearance.current === null) {
    initialAppearance.current = resolvedTheme;
  }

  const iframeUrl = new URL(demoBaseUrl);
  if (page) {
    iframeUrl.pathname = iframeUrl.pathname.replace(/[^/]+\/?$/, page);
  }
  iframeUrl.searchParams.set('locationId', String(locationId));
  iframeUrl.searchParams.set('embed', '1');
  iframeUrl.searchParams.set('full', 'true');
  iframeUrl.searchParams.set('demoAuth', 'parent');
  iframeUrl.searchParams.set(
    'overrideLanguage',
    language === 'zh' ? 'cn' : 'en'
  );
  iframeUrl.searchParams.set(
    'overrideAppearance',
    initialAppearance.current ?? 'light'
  );
  if (experience) iframeUrl.searchParams.set('demoExperience', experience);

  const attachIframe = useCallback((iframe: HTMLIFrameElement | null) => {
    iframeRef.current = iframe;
    frameLoaded.current = false;
  }, []);

  const postMessage = useCallback(
    (message: Record<string, unknown>) => {
      // A lazy iframe initially holds about:blank with the website's origin.
      // Wait for load (or a trusted ready message) before targeting the app.
      if (!frameLoaded.current) return;
      iframeRef.current?.contentWindow?.postMessage(
        { source: DEMO_LOCATION_MESSAGE_SOURCE, ...message },
        demoOrigin
      );
    },
    [demoOrigin]
  );

  const sendLocation = useCallback(() => {
    postMessage({ type: 'select-location', locationId });
  }, [locationId, postMessage]);

  const sendAuthentication = useCallback(
    (token: string | null = accessToken, tokenLocationId = locationId) => {
      if (!token || tokenLocationId !== currentLocationId.current) return;
      postMessage({
        type: 'authenticate',
        access_token: token,
        fingerprint: getDemoFingerprint(),
      });
    },
    [accessToken, locationId, postMessage]
  );

  const sendAppearance = useCallback(() => {
    postMessage({ type: 'set-appearance', appearance: resolvedTheme });
  }, [postMessage, resolvedTheme]);

  const ensureAuthentication = useCallback(() => {
    void ensureSession(locationId)
      .then(session =>
        sendAuthentication(session.access_token, session.location_id)
      )
      .catch(() => undefined);
  }, [ensureSession, locationId, sendAuthentication]);

  useEffect(() => {
    setLocationId(locationId);
    sendLocation();
  }, [locationId, sendLocation, setLocationId]);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame || typeof IntersectionObserver === 'undefined') return;
    const evaluate = () => {
      if (
        frameAuthTrigger.current.update({
          engaged: visitorEngagedRef.current,
          visible: frameVisibleRef.current,
          hasToken: !!accessTokenRef.current,
        })
      ) {
        ensureAuthentication();
      }
    };
    const onEngagement = (event: Event) => {
      if (!isEngagementEvent(event)) return;
      visitorEngagedRef.current = true;
      evaluate();
    };
    const observer = new IntersectionObserver(
      entries => {
        frameVisibleRef.current = entries.some(
          entry => entry.isIntersecting && entry.intersectionRatio >= 0.25
        );
        if (
          frameVisibleRef.current &&
          trackingSurface &&
          !trackedViewRef.current
        ) {
          trackedViewRef.current = true;
          trackInteraction('booking_demo_viewed', trackingSurface, {
            location_id: currentLocationId.current,
            language,
          });
        }
        evaluate();
      },
      { threshold: [0, 0.25] }
    );
    observer.observe(frame);
    const options: AddEventListenerOptions = { passive: true };
    for (const type of [
      'pointerdown',
      'touchstart',
      'keydown',
      'wheel',
      'scroll',
    ]) {
      window.addEventListener(type, onEngagement, options);
    }
    return () => {
      observer.disconnect();
      for (const type of [
        'pointerdown',
        'touchstart',
        'keydown',
        'wheel',
        'scroll',
      ]) {
        window.removeEventListener(type, onEngagement, options);
      }
    };
  }, [ensureAuthentication, language, themeMounted, trackingSurface]);

  useEffect(() => {
    sendAuthentication();
  }, [sendAuthentication]);

  useEffect(() => {
    appliedAppearance.current = null;
    sendAppearance();

    let attempts = 0;
    const retry = window.setInterval(() => {
      if (appliedAppearance.current === resolvedTheme || attempts >= 7) {
        window.clearInterval(retry);
        return;
      }
      attempts += 1;
      sendAppearance();
    }, 250);

    return () => window.clearInterval(retry);
  }, [resolvedTheme, sendAppearance]);

  useEffect(() => {
    if (dataRevision) postMessage({ type: 'refresh-data' });
  }, [dataRevision, postMessage]);

  useEffect(() => {
    const handleDemoMessage = (event: MessageEvent) => {
      if (
        event.source !== iframeRef.current?.contentWindow ||
        event.origin !== demoOrigin ||
        event.data?.source !== DEMO_LOCATION_MESSAGE_SOURCE
      ) {
        return;
      }

      if (event.data?.type === 'ready') {
        frameLoaded.current = true;
        sendLocation();
        sendAuthentication();
        sendAppearance();
        if (authenticateOnReady && !accessToken) ensureAuthentication();
        return;
      }
      if (event.data?.type === 'interaction') {
        if (trackingSurface && !trackedEngagementRef.current) {
          trackedEngagementRef.current = true;
          trackInteraction('booking_demo_engaged', trackingSurface, {
            location_id: locationId,
            language,
          });
        }
        ensureAuthentication();
        return;
      }
      if (event.data?.type === 'authentication-required') {
        if (!authRetryGuard.current.shouldRefresh(accessToken)) {
          console.warn(
            '[demo] iframe keeps rejecting the customer token; not re-minting the session again.'
          );
          return;
        }
        if (!accessToken) {
          if (authenticateOnReady) ensureAuthentication();
          return;
        }
        void refreshSession(locationId)
          .then(session =>
            sendAuthentication(session.access_token, session.location_id)
          )
          .catch(() => undefined);
        return;
      }
      if (
        event.data?.type === 'appearance-applied' &&
        event.data?.appearance === resolvedTheme
      ) {
        appliedAppearance.current = resolvedTheme;
      }
    };

    window.addEventListener('message', handleDemoMessage);
    return () => window.removeEventListener('message', handleDemoMessage);
  }, [
    accessToken,
    authenticateOnReady,
    demoOrigin,
    locationId,
    ensureAuthentication,
    refreshSession,
    resolvedTheme,
    sendAppearance,
    sendAuthentication,
    sendLocation,
    trackingSurface,
    language,
  ]);

  if (!themeMounted) {
    return (
      <div
        aria-hidden="true"
        className={className}
        style={{ ...style, backgroundColor: 'var(--color-background)' }}
      />
    );
  }

  return (
    <iframe
      data-testid="demo-app-frame"
      key={iframeUrl.toString()}
      ref={attachIframe}
      src={iframeUrl.toString()}
      title={title}
      loading={loading}
      className={className}
      style={style}
      allow="clipboard-write; payment"
      onLoad={() => {
        frameLoaded.current = true;
        authRetryGuard.current = createDemoAuthRetryGuard();
        frameAuthTrigger.current = createDemoFrameAuthTrigger();
        sendLocation();
        sendAuthentication();
        sendAppearance();
        if (authenticateOnReady && !accessToken) ensureAuthentication();
      }}
    />
  );
}
